import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  createBlogPost,
  updateBlogPost,
  getBlogPostById,
  uploadImage,
} from '../../services/firebaseService';
import toast from 'react-hot-toast';

const CATEGORIES = [
  'Digital Marketing',
  'SEO',
  'Social Media',
  'Web Development',
  'Branding',
  'Content Marketing',
  'E-Commerce',
  'Technology',
];

export default function AdminBlogForm() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [form, setForm] = useState({
    title: '',
    titleAr: '',
    slug: '',
    excerpt: '',
    excerptAr: '',
    content: '',
    contentAr: '',
    category: '',
    image: '',
    date: new Date().toISOString().split('T')[0],
    readTime: '5 min',
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditing);

  useEffect(() => {
    if (isEditing) {
      fetchPost();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function fetchPost() {
    try {
      const post = await getBlogPostById(id);
      if (!post) {
        toast.error(t('admin.postNotFound'));
        navigate('/admin/blogs');
        return;
      }
      setForm({
        title: post.title || '',
        titleAr: post.titleAr || '',
        slug: post.slug || '',
        excerpt: post.excerpt || '',
        excerptAr: post.excerptAr || '',
        content: post.content || '',
        contentAr: post.contentAr || '',
        category: post.category || '',
        image: post.image || '',
        date: post.date || '',
        readTime: post.readTime || '5 min',
      });
      if (post.image) setImagePreview(post.image);
    } catch {
      toast.error(t('admin.failedLoad'));
    } finally {
      setFetching(false);
    }
  }

  function generateSlug(title) {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'title' && !isEditing) {
        updated.slug = generateSlug(value);
      }
      return updated;
    });
  }

  function handleImageChange(e) {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(t('admin.selectImageFile'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('admin.imageMaxSize'));
      return;
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title || !form.content || !form.slug) {
      toast.error(t('admin.titleSlugContentRequired'));
      return;
    }

    setLoading(true);
    try {
      let imageUrl = form.image;

      if (imageFile) {
        imageUrl = await uploadImage(imageFile, 'blog');
      }

      const postData = {
        ...form,
        image: imageUrl,
      };

      if (isEditing) {
        await updateBlogPost(id, postData);
        toast.success(t('admin.postUpdated'));
      } else {
        await createBlogPost(postData);
        toast.success(t('admin.postCreated'));
      }

      navigate('/admin/blogs');
    } catch (err) {
      toast.error(t('admin.failedSave'));
    } finally {
      setLoading(false);
    }
  }

  if (fetching) {
    return <div className="admin-loading"><div className="spinner"></div></div>;
  }

  return (
    <div className="admin-blog-form">
      <div className="admin-page-header">
        <div>
          <h1>{isEditing ? t('admin.editPost') : t('admin.newPost')}</h1>
          <p>{isEditing ? t('admin.updateYourPost') : t('admin.createNewPost')}</p>
        </div>
        <button onClick={() => navigate('/admin/blogs')} className="btn btn-secondary">
          <i className="fas fa-arrow-left"></i> {t('admin.back')}
        </button>
      </div>

      <form onSubmit={handleSubmit} className="admin-form">
        <div className="admin-form-grid">
          {/* Main content */}
          <div className="admin-form-main">
            <div className="admin-card">
              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label htmlFor="title">{t('admin.titleEn')} *</label>
                  <input
                    id="title"
                    name="title"
                    type="text"
                    value={form.title}
                    onChange={handleChange}
                    placeholder={t('admin.enterPostTitle')}
                    required
                  />
                </div>
                <div className="admin-form-group">
                  <label htmlFor="titleAr">{t('admin.titleAr')}</label>
                  <input
                    id="titleAr"
                    name="titleAr"
                    type="text"
                    value={form.titleAr}
                    onChange={handleChange}
                    placeholder={t('admin.enterPostTitleAr')}
                    dir="rtl"
                  />
                </div>
              </div>

              <div className="admin-form-group">
                <label htmlFor="slug">{t('admin.slug')} *</label>
                <input
                  id="slug"
                  name="slug"
                  type="text"
                  value={form.slug}
                  onChange={handleChange}
                  placeholder={t('admin.postUrlSlug')}
                  required
                />
              </div>

              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label htmlFor="excerpt">{t('admin.excerptEn')}</label>
                  <textarea
                    id="excerpt"
                    name="excerpt"
                    value={form.excerpt}
                    onChange={handleChange}
                    placeholder={t('admin.briefDescription')}
                    rows={3}
                  />
                </div>
                <div className="admin-form-group">
                  <label htmlFor="excerptAr">{t('admin.excerptAr')}</label>
                  <textarea
                    id="excerptAr"
                    name="excerptAr"
                    value={form.excerptAr}
                    onChange={handleChange}
                    placeholder={t('admin.briefDescriptionAr')}
                    rows={3}
                    dir="rtl"
                  />
                </div>
              </div>

              <div className="admin-form-group">
                <label htmlFor="content">{t('admin.contentEn')} *</label>
                <textarea
                  id="content"
                  name="content"
                  value={form.content}
                  onChange={handleChange}
                  placeholder={t('admin.writeContent')}
                  rows={15}
                  required
                />
              </div>

              <div className="admin-form-group">
                <label htmlFor="contentAr">{t('admin.contentAr')}</label>
                <textarea
                  id="contentAr"
                  name="contentAr"
                  value={form.contentAr}
                  onChange={handleChange}
                  placeholder={t('admin.writeContentAr')}
                  rows={15}
                  dir="rtl"
                />
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="admin-form-sidebar">
            <div className="admin-card">
              <h3>{t('admin.settings')}</h3>

              <div className="admin-form-group">
                <label htmlFor="category">{t('admin.category')}</label>
                <select
                  id="category"
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                >
                  <option value="">{t('admin.selectCategory')}</option>
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div className="admin-form-group">
                <label htmlFor="date">{t('admin.date')}</label>
                <input
                  id="date"
                  name="date"
                  type="date"
                  value={form.date}
                  onChange={handleChange}
                />
              </div>

              <div className="admin-form-group">
                <label htmlFor="readTime">{t('admin.readTime')}</label>
                <input
                  id="readTime"
                  name="readTime"
                  type="text"
                  value={form.readTime}
                  onChange={handleChange}
                  placeholder="5 min"
                />
              </div>
            </div>

            <div className="admin-card">
              <h3>{t('admin.featuredImage')}</h3>
              <div className="admin-form-group">
                <div className="admin-image-upload">
                  {imagePreview ? (
                    <div className="admin-image-preview">
                      <img src={imagePreview} alt="Preview" />
                      <button type="button" className="admin-image-remove" onClick={() => {
                        setImageFile(null);
                        setImagePreview('');
                        setForm(prev => ({ ...prev, image: '' }));
                      }}>
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  ) : (
                    <label className="admin-image-dropzone">
                      <i className="fas fa-cloud-upload-alt"></i>
                      <span>{t('admin.clickToUpload')}</span>
                      <small>{t('admin.imageHint')}</small>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        hidden
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>

            <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
              {loading ? (
                <span className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }}></span>
              ) : (
                <>{isEditing ? t('admin.updatePost') : t('admin.publishPost')}</>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
